/**
 * Gaming Mockup Demo Data
 * VOY-11: Pool-based competition system
 */

// Types
export interface GamePool {
  id: string;
  tier: 'bronze' | 'silver' | 'gold';
  maxTripValue: number;
  currentPlayers: number;
  requiredPlayers: number;
  entryFee: number;
  entryFeeCoins: number; // Voy Coins equivalent
  prizes: Prize[];
  status: 'active' | 'filling' | 'almost-full';
  timeRemaining: string;
  destination: string;
  destinationCode: string;
}

export interface Prize {
  rank: string; // "1st", "2nd-3rd", "4th-10th"
  description: string;
  value: number;
  count: number; // How many winners
}

export interface Question {
  id: string;
  text: string;
  options: string[];
  correct: string;
  category: string;
  difficulty: 'easy' | 'medium' | 'hard';
}

export interface Booster {
  id: string;
  name: string;
  icon: string;
  description: string;
  effect: string;
  unlockCriteria?: string;
  available: boolean;
}

export interface LeaderboardEntry {
  rank: number;
  username: string;
  score: number;
  attempts: number;
  prize?: string;
  isYou?: boolean;
}

export type GameMode = 'trivia' | 'memory' | 'puzzle' | 'sequence';

export interface GameModeInfo {
  id: GameMode;
  name: string;
  icon: string;
  description: string;
  difficulty: string;
  estimatedTime: string;
}

// Pool Data
const gamePools: GamePool[] = [
  {
    id: 'bronze-pool',
    tier: 'bronze',
    maxTripValue: 1000,
    currentPlayers: 1847,
    requiredPlayers: 2000,
    entryFee: 1,
    entryFeeCoins: 100,
    prizes: [
      { rank: '1st', description: 'MIA Round-trip Economy', value: 800, count: 1 },
      { rank: '2nd-3rd', description: 'MIA One-way Economy', value: 400, count: 2 }
    ],
    status: 'almost-full',
    timeRemaining: '2h 15m',
    destination: 'Miami',
    destinationCode: 'MIA'
  },
  {
    id: 'silver-pool',
    tier: 'silver',
    maxTripValue: 2000,
    currentPlayers: 3215,
    requiredPlayers: 4000,
    entryFee: 1,
    entryFeeCoins: 100,
    prizes: [
      { rank: '1st', description: 'MIA Round-trip Premium Economy', value: 1500, count: 1 },
      { rank: '2nd', description: 'MIA Round-trip Economy', value: 800, count: 1 }
    ],
    status: 'filling',
    timeRemaining: '6h 45m',
    destination: 'Miami',
    destinationCode: 'MIA'
  },
  {
    id: 'gold-pool',
    tier: 'gold',
    maxTripValue: 3000,
    currentPlayers: 5789,
    requiredPlayers: 6000,
    entryFee: 1,
    entryFeeCoins: 100,
    prizes: [
      { rank: '1st', description: 'MIA Round-trip Business Class', value: 3000, count: 1 }
    ],
    status: 'active',
    timeRemaining: '4h 23m',
    destination: 'Miami',
    destinationCode: 'MIA'
  }
];

// Questions (10 for demo game)
const questions: Question[] = [
  {
    id: '1',
    text: 'What is the capital of Thailand?',
    options: ['Singapore', 'Bangkok', 'Kuala Lumpur', 'Manila'],
    correct: 'Bangkok',
    category: 'Geography',
    difficulty: 'medium'
  },
  {
    id: '2',
    text: 'Which airline has the largest fleet in the world?',
    options: ['American Airlines', 'Delta Air Lines', 'United Airlines', 'Southwest Airlines'],
    correct: 'American Airlines',
    category: 'Aviation',
    difficulty: 'hard'
  },
  {
    id: '3',
    text: 'The Eiffel Tower is located in which city?',
    options: ['London', 'Paris', 'Rome', 'Berlin'],
    correct: 'Paris',
    category: 'Geography',
    difficulty: 'easy'
  },
  {
    id: '4',
    text: 'What is the currency of Japan?',
    options: ['Yuan', 'Won', 'Yen', 'Rupee'],
    correct: 'Yen',
    category: 'Travel',
    difficulty: 'medium'
  },
  {
    id: '5',
    text: 'Which country has the most UNESCO World Heritage Sites?',
    options: ['Italy', 'China', 'Spain', 'France'],
    correct: 'Italy',
    category: 'Culture',
    difficulty: 'hard'
  },
  {
    id: '6',
    text: 'What is the world\'s busiest airport by passenger traffic?',
    options: ['LAX', 'ATL', 'LHR', 'DXB'],
    correct: 'ATL',
    category: 'Aviation',
    difficulty: 'hard'
  },
  {
    id: '7',
    text: 'The Great Barrier Reef is located off the coast of which country?',
    options: ['Australia', 'Indonesia', 'Philippines', 'Fiji'],
    correct: 'Australia',
    category: 'Geography',
    difficulty: 'easy'
  },
  {
    id: '8',
    text: 'What does the airport code "LHR" stand for?',
    options: ['London Heathrow', 'Los Angeles Harbor', 'Las Vegas Regional', 'London Hyde Park'],
    correct: 'London Heathrow',
    category: 'Aviation',
    difficulty: 'medium'
  },
  {
    id: '9',
    text: 'Which ocean is the largest?',
    options: ['Atlantic', 'Pacific', 'Indian', 'Arctic'],
    correct: 'Pacific',
    category: 'Geography',
    difficulty: 'easy'
  },
  {
    id: '10',
    text: 'What is the longest non-stop commercial flight route?',
    options: ['Singapore-NYC', 'Sydney-London', 'Auckland-Doha', 'Perth-London'],
    correct: 'Singapore-NYC',
    category: 'Aviation',
    difficulty: 'hard'
  }
];

// Boosters
const boosters: Booster[] = [
  {
    id: 'fifty-fifty',
    name: '50/50 Eliminator',
    icon: '🛑',
    description: 'Removes 2 incorrect options',
    effect: 'Leaves 1 correct and 1 wrong answer',
    available: true
  },
  {
    id: 'freeze-time',
    name: 'Freeze Time',
    icon: '❄️',
    description: 'Pause the timer for 10 seconds',
    effect: 'Gives extra time to think',
    available: true
  },
  {
    id: 'swap-question',
    name: 'Swap Question',
    icon: '🔄',
    description: 'Skip to a new question',
    effect: 'Get a different question',
    unlockCriteria: 'After 3 correct in a row',
    available: false
  },
  {
    id: 'double-points',
    name: 'Double Points',
    icon: '🚀',
    description: 'Get 2x points for this question',
    effect: 'Double your score if correct',
    unlockCriteria: 'After 5 correct answers',
    available: false
  }
];

// Game Modes
const gameModes: GameModeInfo[] = [
  {
    id: 'trivia',
    name: 'Trivia Challenge',
    icon: '🧠',
    description: 'Test your knowledge with challenging questions',
    difficulty: 'Medium',
    estimatedTime: '5 min'
  },
  {
    id: 'memory',
    name: 'Memory Match',
    icon: '🃏',
    description: 'Match pairs of cards to test your memory',
    difficulty: 'Easy',
    estimatedTime: '3 min'
  },
  {
    id: 'puzzle',
    name: 'Word Puzzle',
    icon: '📝',
    description: 'Solve word puzzles and expand your vocabulary',
    difficulty: 'Medium',
    estimatedTime: '4 min'
  },
  {
    id: 'sequence',
    name: 'Sequence Challenge',
    icon: '🔢',
    description: 'Arrange items in the correct order',
    difficulty: 'Hard',
    estimatedTime: '6 min'
  }
];

// Leaderboard (50 entries)
const leaderboardEntries: LeaderboardEntry[] = [
  { rank: 1, username: '@traveler_pro', score: 95847, attempts: 3, prize: 'MIA Business Class' },
  { rank: 2, username: '@quiz_master', score: 94521, attempts: 2, prize: 'Partial Trip' },
  { rank: 3, username: '@globe_trotter', score: 93105, attempts: 3, prize: 'Partial Trip' },
  { rank: 4, username: '@flight_fan', score: 91234, attempts: 1 },
  { rank: 5, username: '@wanderlust_99', score: 90876, attempts: 3 },
  { rank: 6, username: '@jetsetter_24', score: 89654, attempts: 2 },
  { rank: 7, username: '@travel_buff', score: 88432, attempts: 3 },
  { rank: 8, username: '@adventure_seeker', score: 87901, attempts: 2 },
  { rank: 9, username: '@nomad_life', score: 87654, attempts: 3 },
  { rank: 10, username: '@explorer_x', score: 86543, attempts: 1 },
  { rank: 11, username: '@sky_miles', score: 85432, attempts: 2 },
  { rank: 12, username: '@passport_full', score: 84321, attempts: 3 },
  { rank: 13, username: '@travel_bug', score: 83210, attempts: 2 },
  { rank: 14, username: '@vacation_mode', score: 82109, attempts: 1 },
  { rank: 15, username: '@trip_master', score: 81098, attempts: 3 },
  // More entries (rank 16-246)
  ...Array.from({ length: 231 }, (_, i) => ({
    rank: i + 16,
    username: `@player_${i + 16}`,
    score: 80000 - (i * 250),
    attempts: Math.floor(Math.random() * 3) + 1
  })),
  // Your entry at rank 247
  { rank: 247, username: '@you', score: 87340, attempts: 2, isYou: true }
];

// Export Functions
export function getGamePools(): GamePool[] {
  return gamePools;
}

export function getPoolById(poolId: string): GamePool | undefined {
  return gamePools.find(p => p.id === poolId);
}

export function getQuestions(): Question[] {
  return questions;
}

export function getBoosters(): Booster[] {
  return boosters;
}

export function getAvailableBoosters(): Booster[] {
  // Randomly select 2 available boosters
  const available = boosters.filter(b => b.available);
  return available.slice(0, 2);
}

export function getGameModes(): GameModeInfo[] {
  return gameModes;
}

export function getLeaderboard(): LeaderboardEntry[] {
  return leaderboardEntries;
}

export function getTopPlayers(limit: number = 10): LeaderboardEntry[] {
  return leaderboardEntries.slice(0, limit);
}

export function calculateScore(timeInSeconds: number, isCorrect: boolean): number {
  if (!isCorrect) return 0;
  
  const basePoints = 10000;
  const timePenalty = Math.floor(timeInSeconds * 100); // -1 point per 0.01s
  const score = Math.max(0, basePoints - timePenalty);
  
  return score;
}

export function getTierColor(tier: 'bronze' | 'silver' | 'gold'): {
  gradient: string;
  text: string;
  badge: string;
} {
  switch (tier) {
    case 'bronze':
      return {
        gradient: 'from-amber-700 to-amber-500',
        text: 'text-amber-900',
        badge: 'bg-gradient-to-r from-amber-600 to-amber-400'
      };
    case 'silver':
      return {
        gradient: 'from-gray-500 to-gray-300',
        text: 'text-gray-900',
        badge: 'bg-gradient-to-r from-gray-500 to-gray-300'
      };
    case 'gold':
      return {
        gradient: 'from-yellow-600 to-yellow-400',
        text: 'text-yellow-900',
        badge: 'bg-gradient-to-r from-yellow-500 to-yellow-300'
      };
  }
}

export function getTierEmoji(tier: 'bronze' | 'silver' | 'gold'): string {
  switch (tier) {
    case 'bronze': return '🥉';
    case 'silver': return '🥈';
    case 'gold': return '🥇';
  }
}

// VOY-133: Engagement Features Data

// Daily Streak Data
export interface StreakMilestone {
  days: number;
  reward: string;
  unlocked: boolean;
}

export function getStreakData() {
  return {
    currentStreak: 7,
    todayPlayed: true,
    nextReward: '+100 Voy Coins',
    milestones: [
      { days: 3, reward: 'Free booster', unlocked: true },
      { days: 7, reward: '2x entry discount', unlocked: true },
      { days: 14, reward: 'Premium pool access', unlocked: false },
      { days: 30, reward: 'Exclusive trip draw', unlocked: false }
    ]
  };
}

// Friend Leaderboard Data
export interface FriendEntry {
  rank: number;
  username: string;
  score: number;
  isYou?: boolean;
}

export function getFriendLeaderboard(): FriendEntry[] {
  return [
    { rank: 1, username: '@sarah_travels', score: 94521 },
    { rank: 2, username: '@mike_wanderlust', score: 91234 },
    { rank: 3, username: '@you', score: 87340, isYou: true },
    { rank: 4, username: '@friend_name', score: 82109 },
    { rank: 5, username: '@travel_buddy', score: 78456 }
  ];
}

// Live Event Pool Data
export function getLiveEventPool() {
  return {
    title: 'HOLIDAY SPECIAL POOL',
    prize: '2x Japan Business Class',
    value: 9000,
    multiplier: 3,
    currentPlayers: 4892,
    requiredPlayers: 5000,
    entryFee: 2,
    startsIn: '23 min'
  };
}

// Referral Data
export interface ReferralMilestone {
  friends: number;
  reward: string;
  unlocked: boolean;
}

export function getReferralData() {
  return {
    referralCode: 'VOY-CH2024',
    friendsJoined: 3,
    milestones: [
      { friends: 3, reward: '300 Voy Coins', unlocked: true },
      { friends: 5, reward: 'Free Gold pool entry', unlocked: false },
      { friends: 10, reward: 'Premium boosters', unlocked: false }
    ]
  };
}

// Mystery Box Rewards
export interface MysteryReward {
  type: 'coins' | 'booster' | 'entry' | 'upgrade' | 'jackpot';
  amount: number;
  description: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
}

export function generateMysteryReward(): MysteryReward {
  const random = Math.random() * 100;
  
  if (random < 1) {
    // 1% Jackpot
    return {
      type: 'jackpot',
      amount: 100,
      description: '$100 Travel Credit',
      rarity: 'legendary'
    };
  } else if (random < 6) {
    // 5% Pool upgrade
    return {
      type: 'upgrade',
      amount: 1,
      description: 'Free Pool Upgrade',
      rarity: 'epic'
    };
  } else if (random < 16) {
    // 10% Boosters
    return {
      type: 'booster',
      amount: 2,
      description: '2x Premium Boosters',
      rarity: 'rare'
    };
  } else if (random < 41) {
    // 25% Free entry
    return {
      type: 'entry',
      amount: 1,
      description: 'Free Game Entry',
      rarity: 'rare'
    };
  } else {
    // 60% Coins
    const coinAmount = Math.floor(Math.random() * 450) + 50; // 50-500
    return {
      type: 'coins',
      amount: coinAmount,
      description: `${coinAmount} Voy Coins`,
      rarity: 'common'
    };
  }
}

// Achievements Data
export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlocked: boolean;
  progress?: {
    current: number;
    required: number;
  };
}

export function getAchievements(): Achievement[] {
  return [
    { id: '1', name: 'First Blood', description: 'Win your 1st game', icon: '🎯', unlocked: true },
    { id: '2', name: 'Trivia Master', description: '10 trivia wins', icon: '🧠', unlocked: true },
    { id: '3', name: 'Speed Demon', description: 'Answer in <1s', icon: '⚡', unlocked: true },
    { id: '4', name: 'Pool Shark', description: 'Win Gold pool', icon: '🦈', unlocked: false },
    { id: '5', name: 'Perfectionist', description: '10/10 correct', icon: '💯', unlocked: false },
    { id: '6', name: 'Globetrotter', description: 'Play 100 games', icon: '🌍', unlocked: false, progress: { current: 47, required: 100 } },
    { id: '7', name: 'Hot Streak', description: '5 wins in a row', icon: '🔥', unlocked: false, progress: { current: 3, required: 5 } },
    { id: '8', name: 'Social Butterfly', description: 'Beat 10 friends', icon: '🦋', unlocked: false },
    { id: '9', name: 'Early Bird', description: 'Play at 6 AM', icon: '🐦', unlocked: false }
  ];
}

// Mini Games Data
export interface MiniGame {
  id: string;
  name: string;
  icon: string;
  description: string;
  duration: string;
  reward: string;
  difficulty: 'easy' | 'medium' | 'hard';
}

export function getMiniGames(): MiniGame[] {
  return [
    {
      id: 'sprint',
      name: '3-Question Sprint',
      icon: '🏃',
      description: 'Quick trivia challenge',
      duration: '30s',
      reward: '+50 coins',
      difficulty: 'easy'
    },
    {
      id: 'daily',
      name: 'Daily Trivia',
      icon: '📅',
      description: '1 question per day',
      duration: '10s',
      reward: '+20 coins',
      difficulty: 'medium'
    },
    {
      id: 'speed',
      name: 'Speed Round',
      icon: '⚡',
      description: 'No boosters, pure speed',
      duration: '1 min',
      reward: '+100 coins',
      difficulty: 'hard'
    }
  ];
}

// Game Analytics Data
export interface CategoryPerformance {
  category: string;
  correct: number;
  total: number;
  percentile: number;
}

export function getGameAnalytics() {
  return {
    categories: [
      { category: 'Geography', correct: 9, total: 10, percentile: 85 },
      { category: 'Aviation', correct: 6, total: 10, percentile: 45 },
      { category: 'Culture', correct: 7, total: 10, percentile: 62 }
    ],
    speedPercentile: 78,
    recommendation: 'Practice Aviation category for +2,000 pts potential. Your Geography skills are excellent!',
    bestCategory: 'Geography (9/10)'
  };
}

